import * as Yup from 'yup';
import { AppDispatch } from '@/app/store';
import { Button, Grid, MenuItem, Theme } from '@mui/material';
import { CreateFormProps } from '@/types/create/CreateFormProps';
import { DepartmentDTO } from '@/types/DepartmentDTO';
import { FixedButton } from '@/components/common/button/FixedButton';
import { ForeignStateAlert } from '@/components/create/ForeignStateAlert';
import { Form, Formik, FormikProps, getIn } from 'formik';
import { Helmet } from 'react-helmet-async';
import { LetterAlert } from '@/components/create/letter/LetterAlert';
import { Location, NavigateFunction, useLocation, useNavigate } from 'react-router-dom';
import { MetaType } from '@/types/MetaDTO';
import { MobileBar } from '@/components/home/MobileBar';
import { OutlinedSelect } from '@/components/common/field/OutlinedSelect';
import { PageHeader } from '@/components/common/PageHeader';
import { ROUTE_MAIN_CREATE_ANSWER_AUTHOR } from '@/app/routes';
import { RegionDTO } from '@/types/RegionDTO';
import { RegionSelectField } from '@/components/common/field/RegionSelectField';
import { departmentDTOSchema } from '@/validation/departmentDTOSchema';
import { getDepartment, getRegion, setRegionAndDepartment } from '@/services/createFormSlice';
import { isForeignState } from '@/services/ipLocationSlice';
import { regionDTOSchema } from '@/validation/regionDTOSchema';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { useGetMetaDataQuery } from '@/services/api/metaApiSlice';
import { useGetRegionsQuery } from '@/services/api/createFormApiSlice';
import React, { FC, ReactElement, useCallback, useEffect } from 'react';

interface FormValues {
  region: RegionDTO | null;
  department: DepartmentDTO | null;
}

const validationSchema = Yup.object({
  region: regionDTOSchema,
  department: departmentDTOSchema,
});

export const CreateFormDepartment: FC<CreateFormProps> = (props: CreateFormProps): ReactElement => {
  const { tittle, nextFormPath, previousFormPath, previousFormLabel, isMobile, mobileTittle } = props;

  const dispatch: AppDispatch = useAppDispatch();
  const location: Location = useLocation();
  const navigate: NavigateFunction = useNavigate();

  const department: DepartmentDTO | null = useAppSelector(getDepartment);
  const region: RegionDTO | null = useAppSelector(getRegion);

  const { data: meta } = useGetMetaDataQuery(
    nextFormPath === ROUTE_MAIN_CREATE_ANSWER_AUTHOR
      ? MetaType.CREATE_ANSWER_DEPARTMENT
      : MetaType.CREATE_LETTER_DEPARTMENT,
  );
  const { data: regions, isFetching } = useGetRegionsQuery(null, { refetchOnMountOrArgChange: true });

  const initialValues: FormValues = {
    region: region,
    department: department,
  };

  const handleSubmit = (value: FormValues): void => {
    dispatch(
      setRegionAndDepartment({
        region: value.region,
        department: value.department,
      }),
    );
    if (nextFormPath) {
      navigate(nextFormPath);
    }
  };

  const foreignState: boolean = useAppSelector(isForeignState);

  let handleSetFieldValue: ((field: string, value: unknown) => void) | undefined = undefined;

  const actualizeRegionAndDepartment = useCallback(
    (departmentId: number | string | null | undefined, regions: RegionDTO[] | undefined): void => {
      if (regions && departmentId) {
        const region: RegionDTO | undefined = regions?.find((region: RegionDTO): boolean => {
          return region?.departments?.some((department: DepartmentDTO): boolean => {
            return department.id === parseInt(String(departmentId));
          });
        });
        const department: DepartmentDTO | undefined = region?.departments?.find(
          (department: DepartmentDTO): boolean => {
            return department.id === parseInt(String(departmentId));
          },
        );
        if (region && department) {
          dispatch(
            setRegionAndDepartment({
              region: region,
              department: department,
            }),
          );
          if (handleSetFieldValue) {
            handleSetFieldValue('region', region);
            handleSetFieldValue('department', department);
          }
        }
      }
    },
    [dispatch, handleSetFieldValue],
  );

  useEffect((): void => {
    const params: URLSearchParams = new URLSearchParams(location.search);
    const departmentId: string | null = params.get('departmentId');
    actualizeRegionAndDepartment(departmentId, regions);
  }, [actualizeRegionAndDepartment, dispatch, isFetching, location.search, regions]);

  useEffect((): void => {
    const departmentId: number | undefined = department?.id;
    actualizeRegionAndDepartment(departmentId, regions);
  }, [actualizeRegionAndDepartment, department?.id, regions]);

  return (
    <>
      {meta && (
        <Helmet>
          <title>{meta.title}</title>
          <meta name={'description'} content={meta.description} />
          <meta name={'keywords'} content={meta.keywords} />
        </Helmet>
      )}
      <Formik
        initialValues={initialValues}
        enableReinitialize={true}
        validationSchema={validationSchema}
        validateOnChange={true}
        onSubmit={handleSubmit}>
        {(formikProps: FormikProps<FormValues>) => {
          const { values, errors, touched, submitForm, setFieldValue, setFieldTouched } = formikProps;
          handleSetFieldValue = (field: string, value: unknown): void => {
            setFieldTouched(field, true, false);
            setFieldValue(field, value, true);
          };
          return (
            <Form>
              <Grid
                container={true}
                direction={'column'}
                sx={{
                  paddingBottom: (theme: Theme): string => (isMobile ? theme.spacing(6) : theme.spacing(0)),
                }}>
                <Grid item={true} marginBottom={isMobile ? '48px' : '79px'}>
                  <PageHeader
                    tittle={tittle}
                    previousFormLabel={previousFormLabel}
                    previousFormPath={previousFormPath}
                    isMobile={isMobile}
                    mobileTittle={mobileTittle}
                  />
                </Grid>
                <Grid item={true}>
                  <Grid container={true} spacing={isMobile ? 6 : 3}>
                    <Grid item={true} xs={isMobile ? 12 : 4}>
                      <RegionSelectField
                        regions={regions || []}
                        region={values.region}
                        isLoading={isFetching}
                        error={touched.region && Boolean(errors.region)}
                        helperText={
                          touched.region && Boolean(errors.region)
                            ? getIn(errors.region, 'departments')
                              ? getIn(errors.region, 'departments')
                              : errors.region
                            : ''
                        }
                        onChange={(value: RegionDTO | null): void => {
                          if (handleSetFieldValue) {
                            handleSetFieldValue('region', value);
                          }
                          if (value === null) {
                            setFieldValue('department', null);
                            setFieldTouched('department', false);
                          }
                        }}
                      />
                    </Grid>
                    <Grid item={true} xs={true}>
                      <OutlinedSelect
                        size={'medium'}
                        displayEmpty={true}
                        onChange={(event) => {
                          const selectDepartment = values.region?.departments.find(
                            (department) => department.id === (event.target.value as number),
                          );
                          if (handleSetFieldValue) {
                            handleSetFieldValue('department', selectDepartment);
                          }
                        }}
                        name={'department'}
                        value={values.department ? values.department.id : ''}
                        disabled={Boolean(errors.region) || !values.region}
                        label={'Выберите учреждение'}
                        MenuProps={{ PaperProps: { sx: { marginTop: '3px' } } }}
                        helperText={
                          touched.department &&
                          Boolean(errors.department) &&
                          !(Boolean(errors.region) || !values.region)
                            ? errors.department
                            : ''
                        }
                        error={
                          touched.department &&
                          Boolean(errors.department) &&
                          !(Boolean(errors.region) || !values.region)
                        }>
                        {values.region?.departments &&
                          values.region.departments.map((value) => (
                            <MenuItem key={value.id} value={value.id}>
                              {value.name}
                            </MenuItem>
                          ))}
                      </OutlinedSelect>
                    </Grid>
                    {isMobile ? (
                      <MobileBar>
                        <FixedButton
                          disabled={!(values.department && values.department.allowSending)}
                          color={'primary'}
                          variant={'contained'}
                          onClick={submitForm}>
                          {'Продолжить'}
                        </FixedButton>
                      </MobileBar>
                    ) : (
                      <Grid item={true} xs={true}>
                        <Button
                          disabled={!(values.department && values.department.allowSending)}
                          onClick={submitForm}
                          variant={'contained'}
                          color={'primary'}
                          fullWidth={true}
                          size={'medium'}>
                          {'Продолжить'}
                        </Button>
                      </Grid>
                    )}
                  </Grid>
                  <Grid item={true} mt={isMobile ? 3 : 4}>
                    {values.department?.message && (
                      <LetterAlert error={!values.department.allowSending} isWarn={values.department.isWarnMessage}>
                        {values.department.message}
                      </LetterAlert>
                    )}
                  </Grid>
                  {foreignState && (
                    <Grid item={true} mt={isMobile ? 1 : 2}>
                      <ForeignStateAlert />
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Form>
          );
        }}
      </Formik>
    </>
  );
};
